import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Add as AddIcon,
  Assignment as AssignmentIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { adminApi } from '../../services/adminApi';

interface User {
  id: number;
  email: string;
}

interface Permission {
  id: number;
  name: string;
  description: string;
}

interface Group {
  id: number;
  name: string;
  users?: User[];
  permissions?: Permission[];
}

const AdminDashboard: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openAssignmentDialog, setOpenAssignmentDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<number | ''>('');
  const [selectedGroup, setSelectedGroup] = useState<number | ''>('');
  const [selectedPermission, setSelectedPermission] = useState<number | ''>('');

  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setLoading(true);
      const [groupsResponse, usersResponse, permissionsResponse] = await Promise.all([
        adminApi.getGroups(),
        adminApi.getUsers(),
        adminApi.getPermissions()
      ]);

      if (groupsResponse.success) {
        setGroups(groupsResponse.data);
      }
      if (usersResponse.success) {
        setUsers(usersResponse.data);
      }
      if (permissionsResponse.success) {
        setPermissions(permissionsResponse.data);
      }
    } catch (err) {
      setError('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Validation functions
  const getUsersNotInAnyGroup = () => {
    const usersInGroups = new Set();
    groups.forEach(group => {
      group.users?.forEach(user => usersInGroups.add(user.id));
    });
    return users.filter(user => !usersInGroups.has(user.id));
  };

  const getGroupsWithoutPermissions = () => {
    return groups.filter(group => !group.permissions || group.permissions.length === 0);
  };

  const getGroupsWithoutUsers = () => {
    return groups.filter(group => !group.users || group.users.length === 0);
  };

  const getUsersNotInAnyGroupCount = () => getUsersNotInAnyGroup().length;
  const getGroupsWithoutPermissionsCount = () => getGroupsWithoutPermissions().length;
  const getGroupsWithoutUsersCount = () => getGroupsWithoutUsers().length;

  const handleNavigateToUsers = () => {
    navigate('/users');
  };

  const handleNavigateToGroups = () => {
    navigate('/groups');
  };

  const handleQuickAssignUser = async () => {
    if (!selectedUser || !selectedGroup) {
      setError('Please select both user and group');
      return;
    }

    try {
      const response = await adminApi.addUserToGroup(selectedGroup, selectedUser);
      if (response.success) {
        await fetchData(); // Refresh data
        setSelectedUser('' as number | '');
        setSelectedGroup('' as number | '');
        setOpenAssignmentDialog(false);
        setSuccess('User assigned to group successfully');
      } else {
        setError('Failed to assign user to group');
      }
    } catch (err) {
      setError('Error assigning user to group');
    }
  };

  const handleQuickAssignPermission = async () => {
    if (!selectedPermission || !selectedGroup) {
      setError('Please select both permission and group');
      return;
    }

    try {
      const response = await adminApi.addPermissionToGroup(selectedGroup, selectedPermission);
      if (response.success) {
        await fetchData(); // Refresh data
        setSelectedPermission('' as number | '');
        setSelectedGroup('' as number | '');
        setOpenAssignmentDialog(false);
        setSuccess('Permission assigned to group successfully');
      } else {
        setError('Failed to assign permission to group');
      }
    } catch (err) {
      setError('Error assigning permission to group');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  }

  const usersNotInGroups = getUsersNotInAnyGroup();
  const groupsWithoutPermissions = getGroupsWithoutPermissions();
  const groupsWithoutUsers = getGroupsWithoutUsers();

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <DashboardIcon color="primary" sx={{ fontSize: 40 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Admin Dashboard
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PersonIcon color="primary" />
                <Box>
                  <Typography variant="h4">{users.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Users</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <GroupIcon color="primary" />
                <Box>
                  <Typography variant="h4">{groups.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Groups</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SecurityIcon color="primary" />
                <Box>
                  <Typography variant="h4">{permissions.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Redaction Restrictions</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssignmentIcon color="primary" />
                <Box>
                  <Typography variant="h4">
                    {getUsersNotInAnyGroupCount() + getGroupsWithoutPermissionsCount()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Issues to Fix</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Validation Issues */}
      <Grid container spacing={3}>
        {/* Users not in groups */}
        {usersNotInGroups.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                  border: '2px solid',
                  borderColor: 'primary.main'
                }
              }}
              onClick={handleNavigateToUsers}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <WarningIcon color="warning" />
                  <Typography variant="h6">Users Not Assigned to Groups</Typography>
                  <Chip label={usersNotInGroups.length} color="warning" size="small" />
                  <NavigateNextIcon color="primary" sx={{ ml: 'auto' }} />
                </Box>
                <List dense>
                  {usersNotInGroups.map((user) => (
                    <ListItem key={user.id}>
                      <ListItemIcon>
                        <PersonIcon color="action" />
                      </ListItemIcon>
                      <ListItemText primary={user.email} />
                    </ListItem>
                  ))}
                </List>
                <CardActions sx={{ justifyContent: 'space-between' }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenAssignmentDialog(true);
                    }}
                    size="small"
                  >
                    Quick Assign
                  </Button>
                  <Button
                    variant="outlined"
                    endIcon={<NavigateNextIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigateToUsers();
                    }}
                    size="small"
                    color="primary"
                  >
                    Go to Users
                  </Button>
                </CardActions>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Groups without permissions */}
        {groupsWithoutPermissions.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                  border: '2px solid',
                  borderColor: 'primary.main'
                }
              }}
              onClick={handleNavigateToGroups}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <WarningIcon color="warning" />
                  <Typography variant="h6">Groups Without Redaction Restrictions</Typography>
                  <Chip label={groupsWithoutPermissions.length} color="warning" size="small" />
                  <NavigateNextIcon color="primary" sx={{ ml: 'auto' }} />
                </Box>
                <List dense>
                  {groupsWithoutPermissions.map((group) => (
                    <ListItem key={group.id}>
                      <ListItemIcon>
                        <GroupIcon color="action" />
                      </ListItemIcon>
                      <ListItemText primary={group.name} />
                    </ListItem>
                  ))}
                </List>
                <CardActions sx={{ justifyContent: 'space-between' }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenAssignmentDialog(true);
                    }}
                    size="small"
                  >
                    Quick Assign
                  </Button>
                  <Button
                    variant="outlined"
                    endIcon={<NavigateNextIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigateToGroups();
                    }}
                    size="small"
                    color="primary"
                  >
                    Go to Groups
                  </Button>
                </CardActions>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Groups without users */}
        {groupsWithoutUsers.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                  border: '2px solid',
                  borderColor: 'primary.main'
                }
              }}
              onClick={handleNavigateToGroups}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <WarningIcon color="warning" />
                  <Typography variant="h6">Groups Without Users</Typography>
                  <Chip label={groupsWithoutUsers.length} color="warning" size="small" />
                  <NavigateNextIcon color="primary" sx={{ ml: 'auto' }} />
                </Box>
                <List dense>
                  {groupsWithoutUsers.map((group) => (
                    <ListItem key={group.id}>
                      <ListItemIcon>
                        <GroupIcon color="action" />
                      </ListItemIcon>
                      <ListItemText primary={group.name} />
                    </ListItem>
                  ))}
                </List>
                <CardActions sx={{ justifyContent: 'space-between' }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenAssignmentDialog(true);
                    }}
                    size="small"
                  >
                    Quick Assign
                  </Button>
                  <Button
                    variant="outlined"
                    endIcon={<NavigateNextIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigateToGroups();
                    }}
                    size="small"
                    color="primary"
                  >
                    Go to Groups
                  </Button>
                </CardActions>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* All good status */}
        {usersNotInGroups.length === 0 && groupsWithoutPermissions.length === 0 && groupsWithoutUsers.length === 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
                  <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                  <Typography variant="h6" color="success.main">
                    All validations passed! Every user is in a group and every group has redaction restrictions.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Quick Assignment Dialog */}
      <Dialog open={openAssignmentDialog} onClose={() => setOpenAssignmentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Quick Assignment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="h6">Assign User to Group</Typography>
            <FormControl fullWidth>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedUser}
                label="Select User"
                onChange={(e) => setSelectedUser(e.target.value as number | '')}
              >
                {usersNotInGroups.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Select Group</InputLabel>
              <Select
                value={selectedGroup}
                label="Select Group"
                onChange={(e) => setSelectedGroup(e.target.value as number | '')}
              >
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleQuickAssignUser}
              disabled={!selectedUser || !selectedGroup}
            >
              Assign User to Group
            </Button>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6">Assign Redaction Restriction to Group</Typography>
            <FormControl fullWidth>
              <InputLabel>Select Redaction Restriction</InputLabel>
              <Select
                value={selectedPermission}
                label="Select Redaction Restriction"
                onChange={(e) => setSelectedPermission(e.target.value as number | '')}
              >
                {permissions.map((permission) => (
                  <MenuItem key={permission.id} value={permission.id}>
                    {permission.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Select Group</InputLabel>
              <Select
                value={selectedGroup}
                label="Select Group"
                onChange={(e) => setSelectedGroup(e.target.value as number | '')}
              >
                {groupsWithoutPermissions.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleQuickAssignPermission}
              disabled={!selectedPermission || !selectedGroup}
            >
              Assign Redaction Restriction to Group
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignmentDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard; 